import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, UserPlus } from "@/lib/icons";
import { useAuth } from "@/lib/auth";
import BrandLogo from "@/components/BrandLogo";

const STORAGE_KEY = "pronet_login_prompt";
const WAIT_MS = 60 * 1000;
// Routes where nudging a guest to log in would be redundant or in the way.
const EXCLUDED_PREFIXES = ["/login", "/register", "/admin", "/account/first-login", "/forgot-password"];

function readState() {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeState(value) {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {}
}

export default function LoginPromptModal() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [phase, setPhase] = useState(null); // null | "first" | "final"
  const timerRef = useRef(null);

  const excluded = EXCLUDED_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  useEffect(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }

    // user === null while the session check is still loading; user is truthy once signed in.
    if (user || user === null || excluded) {
      setPhase(null);
      return;
    }

    const stored = readState();

    if (!stored) {
      writeState({ stage: "first" });
      setPhase("first");
      return;
    }

    if (stored.stage === "first") {
      setPhase("first");
      return;
    }

    if (stored.stage === "waiting") {
      const remaining = stored.resumeAt - Date.now();
      if (remaining <= 0) {
        writeState({ stage: "final" });
        setPhase("final");
      } else {
        setPhase(null);
        timerRef.current = setTimeout(() => {
          writeState({ stage: "final" });
          setPhase("final");
        }, remaining);
      }
      return;
    }

    setPhase("final");
  }, [user, excluded, location.pathname]);

  useEffect(() => {
    document.body.style.overflow = phase ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [phase]);

  const dismissFirst = () => {
    if (phase !== "first") return;
    const resumeAt = Date.now() + WAIT_MS;
    writeState({ stage: "waiting", resumeAt });
    setPhase(null);
    timerRef.current = setTimeout(() => {
      writeState({ stage: "final" });
      setPhase("final");
    }, WAIT_MS);
  };

  const goRegister = () => {
    setPhase(null);
    navigate("/register");
  };

  const goLogin = () => {
    setPhase(null);
    navigate("/login");
  };

  if (!phase) return null;

  const dismissible = phase === "first";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={dismissible ? dismissFirst : undefined}
      data-testid="login-prompt-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-sm overflow-hidden rounded-3xl bg-white text-center shadow-[0_30px_80px_-20px_rgba(127,0,255,0.55)] animate-in zoom-in-95 duration-200"
        data-testid="login-prompt-modal"
      >
        {dismissible && (
          <button
            onClick={dismissFirst}
            aria-label="Close"
            data-testid="login-prompt-close"
            className="absolute right-3 top-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur transition-colors hover:bg-white/30"
          >
            <X size={16} />
          </button>
        )}

        <div
          className="px-6 pb-10 pt-9"
          style={{ background: "linear-gradient(135deg, #7F00FF 0%, #9D00FF 100%)" }}
        >
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-white p-2 shadow-lg">
            <BrandLogo size={48} testid="login-prompt-brand" />
          </div>
          <h2 className="mt-4 font-display text-2xl font-semibold text-white">
            {phase === "final" ? "Continue with PRONET" : "Join PRONET"}
          </h2>
        </div>

        <div className="bg-white px-6 py-7">
          <p className="text-sm leading-relaxed text-gray-500">
            {phase === "final"
              ? "Create a free account to keep exploring."
              : "Create your profile and connect with photography professionals across India."}
          </p>
          <button
            onClick={goRegister}
            data-testid="login-prompt-cta"
            className="btn-glow mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white"
          >
            <UserPlus size={15} /> Create account
          </button>
          <button
            onClick={goLogin}
            data-testid="login-prompt-signin"
            className="mt-3 text-xs font-medium text-gray-400 transition-colors hover:text-gray-600"
          >
            Have an account? <span className="font-semibold text-fuchsia-500">Sign in</span>
          </button>
          {dismissible && (
            <button
              onClick={dismissFirst}
              data-testid="login-prompt-later"
              className="mt-2 block w-full text-xs font-medium text-gray-400 transition-colors hover:text-gray-600"
            >
              Later
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
